// src/pages/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ReviewForm from '../components/ReviewForm';
import {
  Star, Heart, ShoppingCart, Truck, RotateCcw, ShieldCheck,
  Minus, Plus, Package, Check, ArrowLeft, X, Home as HomeIcon,
  Share2, Zap, ChevronLeft, ChevronRight, Award, RefreshCw,
  Lock, Info, MessageCircle, ThumbsUp
} from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [imageZoom, setImageZoom] = useState({ x: 0, y: 0, active: false });
  const [cartAdded, setCartAdded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const fetchProductAndReviews = async () => {
    try {
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() };
        setProduct(data);

        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('productId', '==', id),
          where('approved', '==', true)
        );
        const reviewsSnap = await getDocs(reviewsQuery);
        const reviewsData = reviewsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        reviewsData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setReviews(reviewsData);

        if (data.category) {
          const relatedQuery = query(
            collection(db, 'products'),
            where('category', '==', data.category)
          );
          const relatedSnap = await getDocs(relatedQuery);
          setRelatedProducts(
            relatedSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(p => p.id !== id)
              .slice(0, 4)
          );
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchProductAndReviews().finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  const handleBack = () => {
    if (window.history.length > 2) navigate(-1);
    else navigate('/');
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: `Check out ${product.title}`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {}
  };

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, quantity);
    setCartAdded(true);
    setTimeout(() => setCartAdded(false), 2000);
  };

  const handleBuyNow = () => {
    if (!product) return;
    addToCart(product, quantity);
    navigate('/checkout');
  };

  const handleWishlistToggle = () => {
    if (!product) return;
    toggleWishlist(product);
  };

  const handleImageZoom = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setImageZoom({ x, y, active: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
          <p className="mt-4" style={{ color: 'var(--color-secondary-text)' }}>Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="text-center anim-fadeInUp">
          <Package size={64} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>Product Not Found</h2>
          <p className="mb-6" style={{ color: 'var(--color-secondary-text)' }}>
            Ye product exist nahi karta ya remove ho gaya hai.
          </p>
          <button onClick={() => navigate('/')}
            className="px-6 py-3 rounded-lg font-semibold btn-2d"
            style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const isWishlisted = isInWishlist(product.id);

  const images = product.images && product.images.length > 0
    ? product.images
    : (product.image ? [product.image] : ['https://via.placeholder.com/500']);

  const discountPercent = product.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const savedAmount = product.originalPrice && product.originalPrice > product.price
    ? product.originalPrice - product.price
    : 0;

  const incrementQty = () => setQuantity(prev => prev + 1);
  const decrementQty = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  const description = product.description || 'No description available.';
  const isLongDescription = description.length > 250;
  const displayDescription = isLongDescription && !showFullDescription
    ? description.substring(0, 250) + '...'
    : description;

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + (r.rating || 5), 0) / reviews.length).toFixed(1)
    : (product.rating || 5.0);

  return (
    <div className="min-h-screen pb-8 anim-fadeIn"
      style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}>

      {/* ================= STICKY TOP BAR ================= */}
      <div className="sticky top-0 z-40 backdrop-blur-xl shadow-sm anim-slideDown"
        style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderBottom: `1px solid var(--color-border)` }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={handleBack}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-full font-semibold text-sm btn-2d shadow-md"
            style={{ backgroundColor: 'var(--color-button-bg)', color: '#FFFFFF' }}>
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="hidden md:flex items-center gap-1 text-xs flex-1 truncate" style={{ color: 'var(--color-secondary-text)' }}>
            <HomeIcon size={14} />
            <Link to="/" className="hover:opacity-80">Home</Link>
            <span>/</span>
            <span className="truncate">{product.category}</span>
            <span>/</span>
            <span className="font-semibold truncate" style={{ color: 'var(--color-primary-text)' }}>{product.title}</span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleShare}
              className="flex items-center justify-center w-9 h-9 rounded-full btn-2d shadow-sm"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
              {copied ? <Check size={18} /> : <Share2 size={18} />}
            </button>
            <button onClick={() => navigate('/')}
              className="flex items-center justify-center w-9 h-9 rounded-full btn-2d shadow-sm"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
              <X size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

          {/* ================= IMAGES ================= */}
          <div className="anim-fadeInLeft">
            <div className="flex flex-col-reverse md:flex-row gap-4">
              {images.length > 1 && (
                <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-1">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setSelectedImage(i)}
                      className="rounded-xl overflow-hidden transition-all duration-300 shrink-0 btn-2d"
                      style={{
                        border: selectedImage === i ? `2px solid var(--color-accent)` : `2px solid var(--color-border)`,
                        opacity: selectedImage === i ? 1 : 0.6,
                        transform: selectedImage === i ? 'scale(1.05)' : 'scale(1)',
                      }}>
                      <img src={img} alt={`Thumb ${i + 1}`} className="w-16 h-16 md:w-20 md:h-20 object-cover" />
                    </button>
                  ))}
                </div>
              )}

              <div className="relative flex-1">
                <div className="relative rounded-2xl overflow-hidden shadow-xl"
                  style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
                  onMouseMove={handleImageZoom}
                  onMouseLeave={() => setImageZoom({ ...imageZoom, active: false })}>
                  <img src={images[selectedImage]} alt={product.title}
                    className="w-full h-[400px] md:h-[550px] object-cover transition-transform duration-500"
                    style={{
                      transform: imageZoom.active ? 'scale(1.5)' : 'scale(1)',
                      transformOrigin: `${imageZoom.x}% ${imageZoom.y}%`,
                    }} />

                  {discountPercent > 0 && (
                    <div className="absolute top-4 left-4 text-white text-sm font-bold px-3 py-1.5 rounded-full anim-pulseGlow"
                      style={{ backgroundColor: 'var(--color-badge)' }}>
                      -{discountPercent}% OFF
                    </div>
                  )}

                  {product.featured && (
                    <div className="absolute top-4 right-4 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 anim-float shadow-lg"
                      style={{ background: `linear-gradient(135deg, var(--color-button-bg) 0%, var(--color-button-hover-bg) 100%)` }}>
                      <Award size={14} /> FEATURED
                    </div>
                  )}

                  <button onClick={handleWishlistToggle}
                    className="absolute bottom-4 right-4 rounded-full p-3 shadow-lg btn-2d backdrop-blur-md"
                    style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                    <Heart size={22}
                      className={`transition-all duration-300 ${isWishlisted ? 'fill-pink-500 text-pink-500 anim-heartBeat' : ''}`}
                      style={!isWishlisted ? { color: '#DC829A' } : {}} />
                  </button>

                  {images.length > 1 && (
                    <>
                      <button onClick={() => setSelectedImage((selectedImage - 1 + images.length) % images.length)}
                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-md rounded-full p-2 md:p-3 shadow-lg btn-2d">
                        <ChevronLeft size={20} />
                      </button>
                      <button onClick={() => setSelectedImage((selectedImage + 1) % images.length)}
                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-md rounded-full p-2 md:p-3 shadow-lg btn-2d">
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}
                </div>

                {images.length > 1 && (
                  <div className="flex justify-center gap-2 mt-3">
                    {images.map((_, i) => (
                      <button key={i} onClick={() => setSelectedImage(i)}
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: i === selectedImage ? '24px' : '8px',
                          height: '8px',
                          backgroundColor: i === selectedImage ? 'var(--color-accent)' : 'var(--color-border)',
                        }} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ================= DETAILS ================= */}
          <div className="anim-fadeInRight">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs font-bold uppercase px-3 py-1.5 rounded-full anim-popIn"
                style={{ backgroundColor: 'var(--color-button-bg)', color: '#FFFFFF' }}>
                {product.category}
              </span>
              {product.featured && (
                <span className="text-xs font-bold uppercase px-3 py-1.5 rounded-full anim-popIn delay-100"
                  style={{ backgroundColor: 'var(--color-button-hover-bg)', color: '#FFFFFF' }}>
                  ★ Featured
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-4xl font-bold mb-4 leading-tight anim-fadeInUp"
              style={{ color: 'var(--color-primary-text)' }}>
              {product.title}
            </h1>

            <div className="flex items-center gap-3 mb-5 flex-wrap anim-fadeInUp delay-100">
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-full"
                style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
                <Star size={16} className="fill-pink-400 text-pink-400" />
                <span className="font-bold text-sm" style={{ color: 'var(--color-primary-text)' }}>{averageRating}</span>
              </div>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18}
                    className={i < Math.floor(averageRating) ? 'fill-pink-400 text-pink-400' : 'text-gray-300'} />
                ))}
              </div>
              <button onClick={() => setActiveTab('reviews')}
                className="text-sm hover:opacity-80 underline-offset-2 hover:underline"
                style={{ color: 'var(--color-link)' }}>
                {reviews.length} Reviews
              </button>
            </div>

            <div className="rounded-2xl p-5 mb-6 anim-fadeInUp delay-200 card-lift"
              style={{
                backgroundColor: '#FFF2F6',
                border: `1px solid var(--color-button-bg)`,
              }}>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-3xl md:text-4xl font-bold anim-countUp" style={{ color: 'var(--color-link)' }}>
                  Rs. {product.price}
                </span>
                {product.originalPrice && product.originalPrice > product.price && (
                  <>
                    <span className="text-lg line-through" style={{ color: 'var(--color-secondary-text)' }}>
                      Rs. {product.originalPrice}
                    </span>
                    <span className="text-xs font-bold px-3 py-1 rounded-full text-white anim-pulseGlow ml-auto"
                      style={{ backgroundColor: 'var(--color-badge)' }}>
                      Save Rs. {savedAmount}
                    </span>
                  </>
                )}
              </div>
            </div>

            {product.inventory !== undefined && (
              <div className="flex items-center gap-2 mb-6 anim-fadeInUp delay-300">
                <div className={`w-2.5 h-2.5 rounded-full ${product.inventory > 0 ? 'anim-pulseGlow' : ''}`}
                  style={{ backgroundColor: product.inventory > 0 ? 'var(--color-link)' : '#EF4444' }}></div>
                <span className={`text-sm font-semibold`}
                  style={{ color: product.inventory > 0 ? 'var(--color-link)' : '#EF4444' }}>
                  {product.inventory > 0 ? `In Stock — ${product.inventory} available` : 'Out of Stock'}
                </span>
              </div>
            )}

            <div className="mb-6 anim-fadeInUp delay-300">
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-secondary-text)' }}>
                {displayDescription}
              </p>
              {isLongDescription && (
                <button onClick={() => setShowFullDescription(!showFullDescription)}
                  className="text-sm font-semibold mt-2 hover:opacity-80"
                  style={{ color: 'var(--color-link)' }}>
                  {showFullDescription ? 'Read Less ▲' : 'Read More ▼'}
                </button>
              )}
            </div>

            <div className="space-y-3 mb-6 anim-fadeInUp delay-400">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold" style={{ color: 'var(--color-primary-text)' }}>Qty:</span>
                <div className="flex items-center rounded-full shadow-md"
                  style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)' }}>
                  <button onClick={decrementQty} className="p-3 rounded-l-full btn-2d" style={{ color: 'var(--color-primary-text)' }}>
                    <Minus size={16} />
                  </button>
                  <span className="px-4 font-bold text-lg min-w-[40px] text-center" style={{ color: 'var(--color-primary-text)' }}>
                    {quantity}
                  </span>
                  <button onClick={incrementQty} className="p-3 rounded-r-full btn-2d" style={{ color: 'var(--color-primary-text)' }}>
                    <Plus size={16} />
                  </button>
                </div>
                <span className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                  Total: <strong style={{ color: 'var(--color-link)' }}>Rs. {product.price * quantity}</strong>
                </span>
              </div>

              {/* ✅ Add to Cart Button */}
              <button onClick={handleAddToCart}
                className="w-full rounded-full py-4 px-6 font-bold flex items-center justify-center gap-2 btn-2d shadow-xl ripple-btn relative overflow-hidden"
                style={{
                  backgroundColor: cartAdded ? 'var(--color-button-hover-bg)' : 'var(--color-button-bg)',
                  color: '#FFFFFF',
                  transition: 'all 0.3s ease',
                }}>
                {cartAdded ? (
                  <><Check size={22} className="anim-popIn" /> Added to Cart!</>
                ) : (
                  <><ShoppingCart size={20} /> Add to Cart</>
                )}
              </button>

              {/* ✅ Buy Now Button — Same Pink Gradient */}
              <button
                onClick={handleBuyNow}
                className="w-full rounded-full py-4 px-6 font-bold flex items-center justify-center gap-2 btn-2d shadow-xl transition-all"
                style={{
                  background: `linear-gradient(135deg, var(--color-button-bg) 0%, var(--color-button-hover-bg) 100%)`,
                  color: '#FFFFFF',
                }}
              >
                <Zap size={20} /> Buy Now
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6 anim-fadeInUp delay-500">
              <div className="flex flex-col items-center text-center p-3 rounded-xl card-lift"
                style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                <Truck size={22} className="mb-2" style={{ color: 'var(--color-link)' }} />
                <span className="text-xs font-semibold" style={{ color: 'var(--color-primary-text)' }}>Free Shipping</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 rounded-xl card-lift"
                style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                <RotateCcw size={22} className="mb-2" style={{ color: 'var(--color-link)' }} />
                <span className="text-xs font-semibold" style={{ color: 'var(--color-primary-text)' }}>7-Day Returns</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 rounded-xl card-lift"
                style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                <ShieldCheck size={22} className="mb-2" style={{ color: 'var(--color-link)' }} />
                <span className="text-xs font-semibold" style={{ color: 'var(--color-primary-text)' }}>100% Organic</span>
              </div>
            </div>
          </div>
        </div>

        {/* ================= TABS ================= */}
        <div className="mt-12 anim-fadeInUp">
          <div className="flex gap-2 border-b overflow-x-auto pb-0" style={{ borderColor: 'var(--color-border)' }}>
            {[
              { key: 'description', label: 'Description', icon: Info },
              { key: 'reviews', label: `Reviews (${reviews.length})`, icon: MessageCircle },
              { key: 'shipping', label: 'Shipping & Returns', icon: Truck },
            ].map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className="flex items-center gap-2 px-4 py-3 font-semibold text-sm whitespace-nowrap transition-all"
                  style={{
                    color: active ? 'var(--color-link)' : 'var(--color-secondary-text)',
                    borderBottom: active ? `3px solid var(--color-link)` : '3px solid transparent',
                  }}>
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="py-6">
            {activeTab === 'description' && (
              <div className="anim-fadeIn">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--color-primary-text)' }}>
                  Product Description
                </h3>
                <p className="text-sm md:text-base leading-relaxed whitespace-pre-line"
                  style={{ color: 'var(--color-secondary-text)' }}>
                  {description}
                </p>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="anim-fadeIn space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h3 className="text-xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
                    Customer Reviews ({reviews.length})
                  </h3>
                  <button onClick={() => setShowReviewForm(!showReviewForm)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm btn-2d shadow-md"
                    style={{ backgroundColor: 'var(--color-button-bg)', color: '#FFFFFF' }}>
                    <MessageCircle size={14} />
                    {showReviewForm ? 'Hide Form' : 'Write a Review'}
                  </button>
                </div>

                {showReviewForm && (
                  <div className="anim-fadeInUp">
                    <ReviewForm
                      productId={product.id}
                      productTitle={product.title}
                      onSubmitted={fetchProductAndReviews}
                    />
                  </div>
                )}

                {reviews.length === 0 ? (
                  <div className="text-center py-12 rounded-xl"
                    style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                    <MessageCircle size={48} className="mx-auto mb-3 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
                    <p style={{ color: 'var(--color-secondary-text)' }}>
                      No reviews yet. Be the first to review this product!
                    </p>
                  </div>
                ) : (
                  reviews.map((review, i) => (
                    <div key={review.id} className="rounded-xl p-4 card-lift anim-fadeInUp"
                      style={{
                        backgroundColor: '#FFF2F6',
                        border: `1px solid var(--color-button-bg)`,
                        animationDelay: `${i * 100}ms`,
                      }}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: 'var(--color-button-bg)' }}>
                            {review.customerName?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                              {review.customerName || 'Anonymous'}
                            </p>
                            <div className="flex">
                              {[...Array(5)].map((_, star) => (
                                <Star key={star} size={12}
                                  className={star < review.rating ? 'fill-pink-400 text-pink-400' : 'text-gray-300'} />
                              ))}
                            </div>
                          </div>
                        </div>
                        <ThumbsUp size={16} style={{ color: 'var(--color-secondary-text)' }} />
                      </div>
                      <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                        {review.comment}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'shipping' && (
              <div className="anim-fadeIn space-y-4">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--color-primary-text)' }}>
                  Shipping & Returns
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl p-4 card-lift"
                    style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                    <Truck className="mb-2" size={24} style={{ color: 'var(--color-link)' }} />
                    <h4 className="font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>Free Shipping</h4>
                    <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                      Rs. 2000 se upar ke orders par free shipping.
                    </p>
                  </div>
                  <div className="rounded-xl p-4 card-lift"
                    style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                    <RotateCcw className="mb-2" size={24} style={{ color: 'var(--color-link)' }} />
                    <h4 className="font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>7-Day Returns</h4>
                    <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                      7 din ke andar return kar sakte hain.
                    </p>
                  </div>
                  <div className="rounded-xl p-4 card-lift"
                    style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                    <Lock className="mb-2" size={24} style={{ color: 'var(--color-link)' }} />
                    <h4 className="font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>Secure Payment</h4>
                    <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                      COD, Bank Transfer available.
                    </p>
                  </div>
                  <div className="rounded-xl p-4 card-lift"
                    style={{ backgroundColor: '#FFF2F6', border: `1px solid var(--color-button-bg)` }}>
                    <RefreshCw className="mb-2" size={24} style={{ color: 'var(--color-link)' }} />
                    <h4 className="font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>Easy Exchange</h4>
                    <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                      Damaged product ki replacement hogi.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================= RELATED ================= */}
        {relatedProducts.length > 0 && (
          <section className="mt-16 pb-6 anim-fadeInUp">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
                You May Also Like
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((p, i) => (
                <Link key={p.id} to={`/product/${p.id}`}
                  className="rounded-xl shadow-md overflow-hidden card-lift anim-fadeInUp block"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    border: `1px solid var(--color-border)`,
                    animationDelay: `${i * 100}ms`,
                  }}>
                  <div className="overflow-hidden">
                    <img src={p.image || p.images?.[0]} alt={p.title}
                      className="w-full h-40 object-cover hover:scale-110 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm line-clamp-2 mb-1" style={{ color: 'var(--color-primary-text)' }}>
                      {p.title}
                    </h3>
                    <p className="text-sm font-bold" style={{ color: 'var(--color-link)' }}>
                      Rs. {p.price}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="flex justify-center mt-10 pb-6">
          <button onClick={handleBack}
            className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm btn-2d shadow-md"
            style={{ backgroundColor: 'var(--color-button-bg)', color: '#FFFFFF' }}>
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;